import { useState, useRef, useEffect } from "react";
import ReactPlayer from "react-player";
import styled from "@emotion/styled";
import fullscreen1 from "./../../assets/fullscreen-1.svg";
import fullscreen2 from "./../../assets/fullscreen-2.svg";
import { Img } from "@chakra-ui/image";

const VideoWrapper = styled.div`
  position: relative;
  width: 100vw;
  margin: 0 auto 0 auto;
  background: black;
  height: calc(100vh - 150px);
`;

const Controls = styled.div`
  position: absolute;
  bottom: 10px;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 10px;
  flex-direction: row-reverse;
  align-items: end;
  box-sizing: border-box;
`;

const Loading = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
`;

interface Prop {
  poster?: string;
  videoSource: string[];
}

const VideoPlayer = ({ poster, videoSource }: Prop) => {
  const [count, setCount] = useState<number>(0);
  const [url, setUrl] = useState<string>(videoSource[count]);
  const [playing, setPlaying] = useState(true);
  const [loading, setLoading] = useState(true);
  const [buffering, setBuffering] = useState(true);
  const [muted] = useState(true);
  const playerRef = useRef<any>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);

  useEffect(() => {
    if (!loading && !buffering) {
      setPlaying(true);
    }
  }, [loading, buffering]);

  const handleFullScreenChange = () => {
    const doc = document as any;
    const isFullScreen =
      doc.fullscreenElement ||
      doc.webkitFullscreenElement ||
      doc.mozFullScreenElement ||
      doc.msFullscreenElement;

    setIsFullScreen(!!isFullScreen);
  };

  useEffect(() => {
    document.addEventListener("fullscreenchange", handleFullScreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullScreenChange);
    document.addEventListener("mozfullscreenchange", handleFullScreenChange);
    document.addEventListener("msfullscreenchange", handleFullScreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullScreenChange
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullScreenChange
      );
      document.removeEventListener(
        "msfullscreenchange",
        handleFullScreenChange
      );
    };
  }, []);

  const toggleFullscreen = () => {
    if (isFullScreen) {
      handleExitFullscreen();
    } else {
      handleFullscreen();
    }
  };

  const handleExitFullscreen = () => {
    const doc = document as any;
    if (
      doc.fullscreenElement ||
      doc.webkitFullscreenElement ||
      doc.mozFullScreenElement ||
      doc.msFullscreenElement
    ) {
      if (doc.exitFullscreen) {
        doc.exitFullscreen();
      } else if (doc.webkitExitFullscreen) {
        // Safari
        doc.webkitExitFullscreen();
      } else if (doc.mozCancelFullScreen) {
        // Firefox
        doc.mozCancelFullScreen();
      } else if (doc.msExitFullscreen) {
        // IE/Edge
        doc.msExitFullscreen();
      }
      setIsFullScreen(false);
    }
  };

  const handleFullscreen = () => {
    if (playerRef.current) {
      if (playerRef.current.requestFullscreen) {
        playerRef.current.requestFullscreen();
      } else if (playerRef.current.webkitEnterFullscreen) {
        playerRef.current.webkitEnterFullscreen();
      } else {
        console.log("Pantalla completa no es soportada por este navegador.");
      }
    }
  };

  const handleBuffer = () => {
    setBuffering(true);
  };

  const handleBufferEnd = () => {
    setBuffering(false);
  };

  const handleReady = (e: ReactPlayer) => {
    setLoading(false);
  };

  const handleEnded = () => {
    setLoading(true);
    if (count < videoSource.length - 1) {
      setUrl(videoSource[count + 1]);
      setCount(count + 1);
    } else {
      setUrl(videoSource[0]);
      setCount(0);
    }
  };

  return (
    <VideoWrapper data-testid="video-wrapper" ref={playerRef}>
      {loading && <Loading>Loading...</Loading>}
      <ReactPlayer
        url={url}
        playing={playing}
        muted={muted}
        loop={false}
        controls={false}
        onReady={handleReady}
        onEnded={handleEnded}
        onBuffer={handleBuffer}
        onBufferEnd={handleBufferEnd}
        width="100%"
        height="100%"
        playsinline={true}
        poster={poster}
        config={{
          file: {
            attributes: {
              controlsList: "nodownload",
              controls: false,
            },
          },
        }}
      />
      <Controls>
        <Img
          className="fullscreen"
          alt="fullscreen"
          w={8}
          src={isFullScreen ? fullscreen2 : fullscreen1}
          filter={"invert(1)"}
          onClick={toggleFullscreen}
        />
      </Controls>
    </VideoWrapper>
  );
};

export default VideoPlayer;
